import 'isomorphic-fetch';
import queryString from 'query-string';
import { buildQueryString, getApiUrl } from './utils';

const API_URL = getApiUrl();

async function handleResponse(res, blob) {
  const contentType = res.headers.get('content-type');

  let body;

  if (contentType && contentType.indexOf('application/json') !== -1) {
    body = await res.json();
  } else if (blob) {
    body = await res.blob();
  } else {
    body = await res.text();
  }

  if (!res.ok) {
    if (res.status === 401) {
      // Redirect to login.
      // Ignore 401 for fetching current user info.
      if (res.url.indexOf(`${getApiUrl()}/users/me`) !== 0) {
        const loginParams = {
          postGluuLoginRedirectUrl:
            `${window.location.pathname}${window.location.search}`,
        };
        const extra = queryString.parse(window.location.search);
        if (extra.lang) {
          loginParams.lang = extra.lang;
        }
        window.location.href =
          `/selvitys/login?${queryString.stringify(loginParams)}`;
      }
    }
    const rej = { response: res, body, status: res.status };
    return Promise.reject(rej);
  }

  return { response: body };
}

async function callApi(
  endpoint,
  method = 'get',
  bodyData = null,
  headers = null,
  blob = false,
) {
  const fullUrl =
    endpoint.indexOf(API_URL) === -1 ? API_URL + endpoint : endpoint;

  let reqOptions;
  if (method === 'get' || method === 'delete') {
    reqOptions = {
      method,
      headers: {},
    };
  } else if (method === 'post' || method === 'put') {
    /* eslint-disable quote-props */
    reqOptions = {
      method,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bodyData),
    };
    /* eslint-enable quote-props */
  } else if (method === 'file') {
    reqOptions = {
      method: 'POST',
      body: bodyData,
      headers: {},
    };
  } else throw new Error('Unsupported method');
  reqOptions.credentials = 'same-origin';

  if (headers) Object.assign(reqOptions.headers, headers);

  console.debug('[CALLING API]', fullUrl, reqOptions);

  const res = await fetch(fullUrl, reqOptions);

  return handleResponse(res, blob);
}

// /////////////////
// api services
// /////////////////

export async function fetchReport(data) {
  const { country, identifier } = data;
  const url = `/countries/${country}/orgs/${identifier}`;
  const { response } = await callApi(url);

  return response;
}

export async function saveReport(data, token) {
  const { country, identifier } = data;
  const {
    response,
  } = await callApi(`/countries/${country}/orgs/${identifier}`, 'put', data, {
    Authorization: token,
  });

  return response;
}

export async function fetchLocalities() {
  console.debug('API fetch localities start');

  const url = '/localities?type=dict';
  const { response } = await callApi(url);

  console.debug('API fetch localities response:', response);
  return response;
}

export async function fetchRegions() {
  console.debug('API fetch regions start');

  const url = '/regions?type=dict';
  const { response } = await callApi(url);

  console.debug('API fetch regions response:', response);
  return response;
}

export async function fetchReview(data) {
  const { country, identifier, provider, token } = data;
  const { response } = await callApi(
    `/countries/${country}/orgs/${identifier}` +
    `/reviews/me?provider=${provider}&token=${token}`
  );

  return response;
}

export async function saveReview(data) {
  const { country, identifier } = data;
  const { response } = await callApi(
    `/countries/${country}/orgs/${identifier}/reviews`,
    'post',
    data
  );

  return response;
}

export async function savePiace(data) {
  const { country, identifier } = data;
  const { response } = await callApi(
    `/countries/${country}/orgs/${identifier}/piaces`,
    'post',
    data
  );

  return response;
}

export async function getCompanyReviews(data) {
  const { country, identifier } = data;
  const { response } = await callApi(
    `/countries/${country}/orgs/${identifier}/reviews`
  );

  // response.reviews = new Array(10).fill(response.reviews[0]);

  return response;
}

// export async function saveCompanyReview(data) {
//   // const { response } = await callApi(
//   // `/api/${data.country}/${data.identifier}/reviews`, 'get');
//   const response = {};

//   return response;
// }

export async function getUserReviews(data) {
  const { userId } = data;
  const { response } = await callApi(`/users/${userId}/reviews`);

  return response;
}

export async function getChatMessages(data, token = null) {
  const { id: chatId, token: clientToken } = data;
  const clientTokenParameter = clientToken ? `?token=${clientToken}` : '';

  const headers = {};
  if (token) headers.Authorization = token;

  const { response } = await callApi(
    `/chats/${chatId}/messages${clientTokenParameter}`,
    'get',
    null,
    headers
  );

  return response;
}

export async function addChatMessage(data, token = null) {
  const { chatId, token: clientToken } = data;
  const clientTokenParameter = clientToken ? `?token=${clientToken}` : '';

  const headers = {};
  if (token) headers.Authorization = token;

  const { response } = await callApi(
    `/chats/${chatId}/messages${clientTokenParameter}`,
    'post',
    data,
    headers
  );

  return response;
}

export async function getChats(token) {
  const { response } = await callApi('/chats', 'get', null, {
    Authorization: token,
  });

  return response;
}

export async function startChat(data, token) {
  const { reviewId } = data;
  const { response } = await callApi(
    '/chats',
    'post',
    { review_id: reviewId },
    {
      Authorization: token,
    }
  );

  return response;
}

export async function tokenLogin(data) {
  const { token } = data;

  let headers = null;
  if (token) {
    headers = { 'Authorization': token };
  }

  const { response } = await callApi('/users/me', 'get', null, headers);

  return response;
}

export async function fetchTopReviewers(data) {
  const { nUsers } = data;
  const { response } = await callApi(`/topreviewers?n_users=${nUsers}`);

  return response;
}

export async function fetchTopCompanies(data) {
  const { nCompanies, toimialakoodi, paikkakuntakoodi } = data;
  const params = {
    n_companies: nCompanies,
    toimialakoodi,
    paikkakuntakoodi,
  };
  const url = `/topcompanies?${buildQueryString(params)}`;
  const { response } = await callApi(url);

  return response;
}

export async function fetchAuthenticationForms(data, token) {
  const { response } = await callApi('/authentication/forms', 'post', data, {
    Authorization: token,
  });

  return response;
}

// Portfolio =================================================================
export async function fetchPortfolio(meta) {
  const { country: c, identifier: i } = meta;
  const { response } = await callApi(`/countries/${c}/orgs/${i}/portfolio`);
  return response;
}

export async function savePortfolio({ meta, data, token }) {
  const { country, identifier } = meta;
  const url = `/countries/${country}/orgs/${identifier}/portfolio`;
  const options = { Authorization: token };
  const { response } = await callApi(url, 'post', data, options);
  return response;
}

export async function uploadPortfolioImage({
  country, identifier, token, file
}) {
  const data = new FormData();
  data.append('file', file);

  const url = `/countries/${country}/orgs/${identifier}/images`;
  const res = await fetch(API_URL + url, {
    method: 'post',
    body: data,
    headers: {
      Authorization: token,
    },
  });

  const { response } = await handleResponse(res);
  return response;
}

export async function enablePortfolio({ country, identifier, token }) {
  const url = `/countries/${country}/orgs/${identifier}/portfolio/enable`;
  const options = { Authorization: token };
  const { response } = await callApi(url, 'post', null, options);
  return response;
}

export async function disablePortfolio({ country, identifier, token }) {
  const url = `/countries/${country}/orgs/${identifier}/portfolio/disable`;
  const options = { Authorization: token };
  const { response } = await callApi(url, 'get', null, options);
  return response;
}

export async function showPortfolio({ country, identifier, token }) {
  const url = `/countries/${country}/orgs/${identifier}/portfolio/show`;
  const options = { Authorization: token };
  const { response } = await callApi(url, 'post', null, options);
  return response;
}

export async function hidePortfolio({ country, identifier, token }) {
  const url = `/countries/${country}/orgs/${identifier}/portfolio/hide`;
  const options = { Authorization: token };
  const { response } = await callApi(url, 'get', null, options);
  return response;
}

export async function fetchKeywords({ country, identifier }) {
  const url = `/countries/${country}/orgs/${identifier}/keywords`;
  const { response } = await callApi(url);
  return response;
}

export async function saveKeywords({ country, identifier, token, keywords }) {
  const url = `/countries/${country}/orgs/${identifier}/keywords`;
  const options = { Authorization: token };
  const { response } = await callApi(url, 'post', { keywords }, options);
  return response;
}

export async function fetchCompanyRegions({ country, identifier }) {
  const url = `/countries/${country}/orgs/${identifier}/regions`;
  const { response } = await callApi(url);
  return response;
}

export async function saveCompanyRegions(
  { country, identifier, token, regions }
) {
  const url = `/countries/${country}/orgs/${identifier}/regions`;
  const options = { 'Authorization': token };
  const { response } = await callApi(url, 'post', { regions }, options);
  return response;
}

export async function activateLicense({ license, token }) {
  const url = '/license';
  const options = { 'Authorization': token };
  const { response } = await callApi(url, 'post', { license }, options);
  return response;
}


export async function deactivateLicense({ license, token }) {
  const url = `/license/${license.id}`;
  const options = { 'Authorization': token };
  const { response } = await callApi(url, 'delete', null, options);
  return response;
}


export async function updateLicense({ license, updatedLicense, token }) {
  const url = `/license/${license.id}`;
  const options = { 'Authorization': token };
  const { response } = await callApi(
    url, 'post', { license: updatedLicense }, options
  );
  return response;
}

export async function upgradeLicense({ license, newLicense, token }) {
  const url = `/license/${license.id}`;
  const options = { 'Authorization': token };
  const { response } =
    await callApi(url, 'put', { license: newLicense }, options);
  return response;
}

export async function fetchLicenses({
  andFilters, orFilters, pagination, searchTerms, token,
}) {
  const options = { 'Authorization': token };
  const params = {
    andFilter: andFilters,
    orFilter: orFilters,
    ...searchTerms,
    ...pagination,
  };
  const url = `/license?${queryString.stringify(params)}`;
  const { response } = await callApi(url, 'get', null, options);
  return response;
}

export async function fetchCompanyLicenses({ token, country, identifier }) {
  const options = { 'Authorization': token };
  const url = `/countries/${country}/orgs/${identifier}/licenses`;
  const { response } = await callApi(url, 'get', null, options);
  return response;
}

export async function fetchLicenseLogs({
  andFilters, orFilters, pagination, searchTerms, token,
}) {
  const options = { 'Authorization': token };
  const params = {
    andFilter: andFilters,
    orFilter: orFilters,
    ...searchTerms,
    ...pagination,
  };
  const url = `/license-log?${queryString.stringify(params)}`;
  const { response } = await callApi(url, 'get', null, options);
  return response;
}

export async function fetchLicenseTypes({ token }) {
  const url = '/license-types';
  const options = { 'Authorization': token };
  const { response } = await callApi(url, 'get', null, options);
  return response;
}

export async function fetchSellers({ appendOrgInfo, token }) {
  const options = { 'Authorization': token };
  const params = { appendOrgInfo };
  const url = `/license/sellers?${queryString.stringify(params)}`;
  const { response } = await callApi(url, 'get', null, options);
  return response;
}

export async function exportLicenses({
  andFilters: andFilter, orFilters: orFilter, pagination, token, type,
}) {
  const options = { 'Authorization': token };
  const params = { andFilter, orFilter, ...pagination, type };
  const query = queryString.stringify(params);
  const url = `/license/export/xlsx?${query}`;
  const { response } = await callApi(url, 'get', null, options, true);
  return response;
}

export async function saveLicensePayment({ token, file }) {
  const options = { 'Authorization': token };
  const url = '/license/payment/import';
  const body = new FormData();
  body.append('csv', file);
  const { response } = await callApi(url, 'file', body, options);
  return response;
}

export async function fetchLicensePayments({ token, licenseId }) {
  const options = { 'Authorization': token };
  const url = `/license/${licenseId}/payments`;
  const { response } = await callApi(url, 'get', null, options);
  return response;
}

export async function importLicenses({ token, file, overwrite }) {
  const options = { 'Authorization': token };
  const body = new FormData();
  body.append('xlsx', file);
  const params = { 'overwrite': overwrite };
  const url = `/license/migrate-old?${buildQueryString(params)}`;
  const { response } = await callApi(url, 'file', body, options);
  return response;
}

export async function fetchContactPersons({ token, country, identifier }) {
  const url = `/countries/${country}/orgs/${identifier}/contact-persons`;
  const options = { 'Authorization': token };
  const { response } = await callApi(url, 'get', null, options);
  return response;
}

export async function addContactPerson({
  token, contactPerson, country, identifier,
}) {
  const url = `/countries/${country}/orgs/${identifier}/contact-persons`;
  const options = { 'Authorization': token };
  const body = { contactPerson };
  const { response } = await callApi(url, 'post', body, options);
  return response;
}

export async function updateContactPerson({ token, contactPerson }) {
  const url = `/contact-person/${contactPerson.id}`;
  const options = { 'Authorization': token };
  const body = { contactPerson };
  const { response } = await callApi(url, 'post', body, options);
  return response;
}

export async function fetchRPQuickCheck({ company: { country, identifier } }) {
  const url = `/countries/${country}/orgs/${identifier}/rp-quick-check`;
  const { response } = await callApi(url, 'get');
  return response;
}

export async function fetchCLReport({ country, format, identifier, token }) {
  const options = { 'Authorization': token };
  const url = `/countries/${country}/orgs/${identifier}/clreport/${format}`;
  const { response } = await callApi(url, 'get', null, options, true);
  return response;
}
